import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useTenant } from "../../contexts/TenantContext";
import { TenantShell, Stepper, ECO_STEPS } from "../../components/tenant/TenantShell";
import { StepBusiness } from "../../components/tenant/StepBusiness";
import { StepLpoApplication } from "../../components/tenant/StepLpoApplication";
import { StepDocuments } from "../../components/tenant/StepDocuments";
import { StepLpoDocuments } from "../../components/tenant/StepLpoDocuments";
import { StepScore } from "../../components/tenant/StepScore";
import { StepOutcome } from "../../components/tenant/StepOutcome";
import { StepLpoOutcome } from "../../components/tenant/StepLpoOutcome";
import { StepNDA } from "../../components/tenant/StepNDA";
import { StepEcoDocuments } from "../../components/tenant/StepEcoDocuments";
import { StepEcoResults } from "../../components/tenant/StepEcoResults";
import { tapi, apiErr } from "../../lib/tenant";

function EcosystemJourney({ me, refreshMe, step, setStep }) {
  const [resubmit, setResubmit] = useState(false);
  return (
    <>
      <p className="mb-2 text-xs font-semibold text-neutral-500" data-testid="tenant-journey-greeting">Hi {me.user.name?.split(" ")[0]}, let's see who we can connect you to.</p>
      <Stepper current={step} steps={ECO_STEPS} />
      {step === 1 && <StepNDA onAccepted={async () => { await refreshMe(); setStep(2); }} />}
      {step === 2 && <StepEcoDocuments documents={me.documents} onChanged={refreshMe} resubmit={resubmit}
        onBack={resubmit ? () => { setResubmit(false); setStep(3); } : null}
        onSubmitted={async () => { await refreshMe(); setResubmit(false); setStep(3); }} />}
      {step === 3 && (me.outcome
        ? <StepEcoResults me={me} onRefreshMe={refreshMe} onResubmit={() => { setResubmit(true); setStep(2); }} />
        : <StepEcoDocuments documents={me.documents} onChanged={refreshMe} onSubmitted={async () => { await refreshMe(); }} />)}
    </>
  );
}

export default function TenantJourney() {
  const { me, refreshMe, to, sub, config } = useTenant();
  const [step, setStep] = useState(null);
  const [rerun, setRerun] = useState(false);
  const [params, setParams] = useSearchParams();
  const rerunRef = params.get("rerun_ref");

  useEffect(() => { if (me && step === null) setStep(me.step); }, [me, step]);

  useEffect(() => {
    if (!rerunRef || !me) return;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await tapi.post(`/t/${sub}/reruns/poll/${rerunRef}`);
        if (data.payment_status === "paid") {
          toast.success(config.prequal_flow === "lpo" ? "Payment received — upload your new documents below" : `Payment received — ${data.reruns.total_left} re-runs available`);
          await refreshMe(); setParams({});
          if (config.prequal_flow === "lpo") { setRerun(true); setStep(2); }
          return;
        }
        if (data.payment_status === "failed" || tries >= 5) { toast.error("Payment not completed — no re-runs were added."); setParams({}); return; }
        tries += 1; setTimeout(poll, 2500);
      } catch (e) { toast.error(apiErr(e, "Couldn't confirm payment")); setParams({}); }
    };
    poll();
  }, [rerunRef]); // eslint-disable-line react-hooks/exhaustive-deps

  if (me === undefined) return <TenantShell><div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-neutral-400" /></div></TenantShell>;
  if (me === null) return <Navigate to={to("/start?mode=login")} replace />;
  if (step === null) return null;

  if (config.partner_type === "ecosystem") {
    return <TenantShell><EcosystemJourney me={me} refreshMe={refreshMe} step={step} setStep={setStep} /></TenantShell>;
  }

  const scored = me.score != null;
  const startRerun = () => { setRerun(true); setStep(2); };
  const finishRerun = async () => { await refreshMe(); setRerun(false); setStep(4); };
  return (
    <TenantShell>
      <p className="mb-2 text-xs font-semibold text-neutral-500" data-testid="tenant-journey-greeting">Hi {me.user.name?.split(" ")[0]}, let's see if you qualify.</p>
      <Stepper current={step} />
      {rerun && step !== 4 && (
        <div className="mb-4 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="tenant-rerun-banner">
          <RefreshCw size={16} className="mt-0.5 shrink-0" />
          <p><b>Re-check in progress.</b> Add or replace your documents, then re-score. Your previous score ({me.score}) will be replaced. {me.reruns?.total_left} re-run{me.reruns?.total_left === 1 ? "" : "s"} left.</p>
        </div>
      )}
      {step === 1 && (config.prequal_flow === "lpo"
        ? <StepLpoApplication initial={me.business} onSaved={async () => { await refreshMe(); setStep(2); }} />
        : <StepBusiness initial={me.business} onSaved={async () => { await refreshMe(); setStep(2); }} />)}
      {step === 2 && (config.prequal_flow === "lpo"
        ? <StepLpoDocuments documents={me.documents} onChanged={refreshMe} onBack={() => (rerun ? setStep(4) : setStep(1))} onSubmit={() => setStep(3)} />
        : <StepDocuments documents={me.documents} onChanged={refreshMe} onBack={() => (rerun ? setStep(4) : setStep(1))} onNext={() => setStep(3)} />)}
      {step === 3 && <StepScore me={me} rerun={rerun} autoStart={config.prequal_flow === "lpo"} onBack={() => setStep(2)} onScored={rerun ? finishRerun : async () => { await refreshMe(); setStep(4); }} />}
      {step === 4 && (scored
        ? (config.prequal_flow === "lpo" ? <StepLpoOutcome me={me} onRerun={startRerun} /> : <StepOutcome me={me} onRefreshMe={refreshMe} onRerun={startRerun} />)
        : <StepScore me={me} onBack={() => setStep(2)} onScored={async () => { await refreshMe(); }} autoStart={config.prequal_flow === "lpo"} />)}
    </TenantShell>
  );
}
